import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { usePrefs, useT } from "@/lib/prefs";
import { useAuth } from "@/lib/auth";
import { LANGUAGE_META, CURRENCY_META } from "@/lib/i18n";
import { Logo } from "@/components/brand";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/utils";

export default function Preferences() {
  const t = useT();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language, currency, rates, ratesUpdatedAt, setPrefs } = usePrefs();

  const change = async (p) => {
    await setPrefs(p);
    toast.success(t("prefs.saved"));
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-6">
        <PageHeader title={t("prefs.title")} onBack={() => navigate(-1)} right={<Logo size={24} />} />

        <div className="mb-8">
          <div className="font-display text-lg text-ink mb-3">{t("prefs.language")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {LANGUAGE_META.map((l) => (
              <button key={l.key} data-testid={`pref-lang-${l.key}`} onClick={() => change({ language: l.key })}
                className={cn("flex items-center gap-3 p-3 rounded-md border text-left", language === l.key ? "border-brand bg-brand-soft2" : "border-line")}>
                <span className="text-xl">{l.flag}</span>
                <span className="font-body font-semibold text-sm text-ink flex-1">{l.label}</span>
                {language === l.key ? <Check size={18} className="text-brand" /> : null}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="font-display text-lg text-ink mb-3">{t("prefs.currency")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CURRENCY_META.map((c) => (
              <button key={c.key} data-testid={`pref-currency-${c.key}`} onClick={() => change({ currency: c.key })}
                className={cn("flex items-center gap-3 p-3 rounded-md border text-left", currency === c.key ? "border-brand bg-brand-soft2" : "border-line")}>
                <span className="w-9 text-center font-display text-brand">{c.symbol}</span>
                <div className="flex-1">
                  <div className="font-body font-semibold text-sm text-ink">{c.key} · {c.name}</div>
                  {c.key !== "ZAR" && rates[c.key] ? <div className="font-body text-xs text-mutedgrey">{t("prefs.rate")} {c.symbol}{Number(rates[c.key]).toFixed(c.decimals + 2)}</div> : null}
                </div>
                {currency === c.key ? <Check size={18} className="text-brand" /> : null}
              </button>
            ))}
          </div>
        </div>

        <p className="font-body text-xs text-mutedgrey mt-6">{t("prefs.note")}</p>
        {ratesUpdatedAt ? <p className="font-body text-[11px] text-mutedgrey mt-1">Rates updated {new Date(ratesUpdatedAt).toLocaleDateString()}</p> : null}
        {!user ? <p className="font-body text-xs text-mutedgrey mt-4">Sign in to save these preferences to your account.</p> : null}
      </div>
    </div>
  );
}
